import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ExternalLink, ArrowRight, Search, X, Shield, Zap, CreditCard, Users } from 'lucide-react';
import { GAMESERVERS } from '@/data/gameservers';
import { SEOHead } from '@/components/SEOHead';
import { ReviewForestWidget } from '@/components/ReviewForestWidget';
import { useTranslation } from '@/lib/i18n';

const GameCard = ({ game, index, t }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.03, duration: 0.3 }}
    className={`group relative overflow-hidden rounded-xl border border-border bg-card hover:border-primary/50 transition-all duration-300 ${
      game.featured ? 'md:col-span-2 md:row-span-2' : ''
    }`}
    data-testid={`game-card-${game.slug}`}
  >
    <Link to={`/gameservers/${game.slug}`} className="block h-full">
      <div className={`overflow-hidden bg-black/20 ${game.featured ? 'aspect-[16/8]' : 'aspect-[16/10]'}`}>
        <img
          src={game.image}
          alt={game.name}
          className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />
      </div>

      <div className="p-4">
        <div className="flex flex-wrap gap-2 mb-2">
          {game.tags.map((tag) => (
            <Badge key={tag} variant="secondary" className="text-xs">
              {tag}
            </Badge>
          ))}
          {game.featured && (
            <Badge className="text-xs bg-primary">{t('gameservers.badge_popular')}</Badge>
          )}
          {game.price && (
            <Badge className="text-xs bg-primary">from €{game.price}{game.priceUnit}</Badge>
          )}
        </div>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold group-hover:text-primary transition-colors">
            {game.name}
          </h3>
          <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
        </div>
      </div>

      <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
    </Link>
  </motion.div>
);

export default function GameserversPage() {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  const featureBadges = [
    { icon: Shield, key: 'feat_ddos' },
    { icon: Zap,    key: 'feat_setup' },
    { icon: CreditCard, key: 'feat_prepaid' },
    { icon: Users,  key: 'feat_support' },
  ];

  const filtered = query.trim()
    ? GAMESERVERS.filter((g) =>
        g.name.toLowerCase().includes(query.toLowerCase()) ||
        g.tags.some((tt) => tt.toLowerCase().includes(query.toLowerCase()))
      )
    : GAMESERVERS;

  return (
    <Layout>
      <SEOHead
        title="Game Server Hosting: Minecraft, Rust & more"
        description="Game servers for Minecraft, Rust, ARK, CS2, Valheim and many more – instant setup, DDoS protection in Frankfurt, prepaid with no contract."
        canonical="/gameservers"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: 'Gameserver Offers',
          url: 'https://deinserverhost.com/gameservers',
          itemListElement: GAMESERVERS.map((g, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: g.name,
            url: `https://deinserverhost.com/gameservers/${g.slug}`,
          })),
        }}
      />
      <div className="section-spacing">
        <div className="container-main">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-6xl font-black mb-4">
              {t('gameservers.title')} <span className="text-gradient">{t('gameservers.titleHighlight')}</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              {t('gameservers.subtitle')}
            </p>

            {/* Feature badges */}
            <div className="flex flex-wrap justify-center gap-3">
              {featureBadges.map(({ icon: Icon, key }) => (
                <div
                  key={key}
                  className="flex items-center gap-2 px-4 py-2 rounded-full glass border border-border hover:border-primary/30 transition-colors"
                >
                  <Icon className="w-4 h-4 text-primary shrink-0" />
                  <span className="text-sm font-medium">{t(`gameservers.${key}`)}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Search */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="relative max-w-lg mx-auto mb-10"
          >
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('gameservers.search_placeholder')}
              className="pl-10 pr-10 h-12 rounded-xl bg-card border-border focus:border-primary text-sm"
              data-testid="gameserver-search-input"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={t('gameservers.search_clear')}
                data-testid="gameserver-search-clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </motion.div>

          {/* Games Grid */}
          <AnimatePresence mode="wait">
            {filtered.length > 0 ? (
              <motion.div
                key="grid"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
              >
                {filtered.map((game, index) => (
                  <GameCard key={game.slug} game={game} index={index} t={t} />
                ))}
              </motion.div>
            ) : (
              <motion.div
                key="empty"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="text-center py-20"
                data-testid="gameserver-no-results"
              >
                <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-40" />
                <p className="text-lg font-semibold mb-2">
                  {t('gameservers.noResults')} "{query}"
                </p>
                <p className="text-sm text-muted-foreground mb-6">
                  {t('gameservers.noResults2')}
                </p>
                <Button variant="outline" onClick={() => setQuery('')} className="border-primary/30 hover:border-primary">
                  {t('gameservers.search_clear')}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Why section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-20 mb-12"
          >
            <h2 className="text-2xl font-bold text-center mb-10">{t('gameservers.whyTitle')}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((n) => (
                <div key={n} className="glass rounded-xl p-6 text-center border border-border hover:border-primary/30 transition-colors">
                  <h3 className="font-bold mb-2 text-sm">{t(`gameservers.why${n}_title`)}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{t(`gameservers.why${n}_desc`)}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Reviews */}
          <ReviewForestWidget className="mb-12" />

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-center p-8 glass rounded-2xl"
          >
            <h2 className="text-2xl font-bold mb-2">{t('gameservers.ctaTitle')}</h2>
            <p className="text-muted-foreground mb-6 max-w-lg mx-auto text-sm">
              {t('gameservers.ctaDesc')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="https://deinserverhost.de/store/submitticket.php"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="outline" className="border-primary/30 hover:border-primary">
                  {t('common.learnMore')}
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <Link to="/product-finder">
                <Button className="bg-primary hover:bg-primary/90 glow-hover">
                  {t('gameservers.ctaBtn')}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
}
